import { format } from 'date-fns';
import { Calendar, Code, Download, Eye, File, FileText, User } from 'lucide-react';
import { useState, useEffect } from 'react';

import { MarkdownViewer } from '@/components/markdown-viewer';
import { OpenAPIViewer } from '@/components/openapi-viewer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { detectFileType } from '@/utils/file-utils';

interface ShareFile {
  id: string;
  filename: string;
  content_type: string;
  byte_size: number;
  created_at: string;
  url: string;
}

interface PublicShareProps {
  share: {
    title: string;
    description: string;
    created_at: string;
    expires_at: string | null;
    view_count: number;
    files: ShareFile[];
    user: {
      name: string;
    };
  };
}

export default function PublicShare({ share }: PublicShareProps) {
  const [selectedFile, setSelectedFile] = useState<ShareFile | null>(share.files[0] || null);
  const [fileContent, setFileContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  
  // Auto-load first file content on mount
  useEffect(() => {
    if (share.files.length > 0) {
      void loadFileContent(share.files[0]);
    }
  }, []);

  const loadFileContent = async (file: ShareFile) => {
    setSelectedFile(file);
    setLoading(true);
    
    try {
      const response = await fetch(file.url);
      const content = await response.text();
      setFileContent(content);
    } catch (error) {
      console.error('Error loading file:', error);
      setFileContent('Error loading file content');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'md' || ext === 'txt') return <FileText className="w-5 h-5" />;
    if (['json', 'yaml', 'yml'].includes(ext ?? '')) return <Code className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderFileContent = () => {
    if (!selectedFile || !fileContent) return null;

    const fileType = detectFileType(selectedFile.filename, fileContent);
    
    switch (fileType) {
      case 'markdown':
        return <MarkdownViewer content={fileContent} />;
      
      case 'openapi':
        return <OpenAPIViewer spec={fileContent} />;
      
      default:
        // Default to code view
        return (
          <Card>
            <CardContent className="p-6">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                <code>{fileContent}</code>
              </pre>
            </CardContent>
          </Card>
        );
    }
  };

  const isExpired = share.expires_at && new Date(share.expires_at) < new Date();

  if (isExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-red-600 dark:text-red-400">Share Expired</CardTitle>
            <CardDescription>
              This share link has expired and is no longer available.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">{share.title}</h1>
          {share.description && (
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
              {share.description}
            </p>
          )}
          
          {/* Meta information */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span className="flex items-center">
              <User className="w-4 h-4 mr-1" />
              Shared by {share.user.name}
            </span>
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {format(new Date(share.created_at), 'PPP')}
            </span>
            <span className="flex items-center">
              <Eye className="w-4 h-4 mr-1" />
              {share.view_count} views
            </span>
            {share.expires_at && (
              <span className="flex items-center text-orange-600 dark:text-orange-400">
                Expires {format(new Date(share.expires_at), 'PPP')}
              </span>
            )}
          </div>
        </div>

        {/* File selector - horizontal scrollable */}
        {share.files.length > 0 && (
          <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3 overflow-x-auto pb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Files:</span>
              {share.files.map((file) => (
                <button
                  key={file.id}
                  onClick={() => void loadFileContent(file)}
                  className={`inline-flex items-center px-4 py-2 rounded-lg border whitespace-nowrap transition-all ${
                    selectedFile?.id === file.id
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-600 shadow-sm'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  {getFileIcon(file.filename)}
                  <span className="ml-2 text-sm font-medium">{file.filename}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    ({formatFileSize(file.byte_size)})
                  </span>
                </button>
              ))}
              {selectedFile && (
                <a
                  href={selectedFile.url}
                  download={selectedFile.filename}
                  className="inline-flex items-center px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </a>
              )}
            </div>
          </div>
        )}

        {/* Main content viewer - full width */}
        <div className="w-full">
          {loading ? (
            <Card className="shadow-lg">
              <CardContent className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">Loading file content...</p>
              </CardContent>
            </Card>
          ) : selectedFile ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              {renderFileContent()}
            </div>
          ) : (
            <Card className="shadow-lg">
              <CardContent className="p-16 text-center">
                <FileText className="w-20 h-20 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  {share.files.length > 0
                    ? 'Select a file to view its contents'
                    : 'No files attached to this share'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}